import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { toast } from "sonner";

import {
  getGetMeQueryKey,
  getListPracticeMembersQueryKey,
  useCreateInvitation,
  useListPracticeMembers,
  useUpdatePractice,
} from "@elation/api-client/src/stitch-web";

import { useCurrentPractice, usePracticeId } from "@src/features/practices";
import { getDisplayErrorMessage } from "@src/shared/utils/errors";

import type { InviteMemberValues } from "../../ui/pages/v2/TeamSettingsPage";

export function useTeamSettingsFlow() {
  const practiceId = usePracticeId();
  const practice = useCurrentPractice();
  const queryClient = useQueryClient();
  const updatePractice = useUpdatePractice();
  const createInvitation = useCreateInvitation();
  const [inviteSent, setInviteSent] = useState(false);

  const membersQuery = useListPracticeMembers(
    practiceId,
    {},
    { query: { enabled: Boolean(practiceId) } },
  );

  const members = (membersQuery.data?.data?.members ?? []).map((m) => {
    if ("userId" in m) {
      return {
        id: m.userId,
        email: m.email,
        firstName: m.firstName,
        lastName: m.lastName,
        status: m.isActive ? ("active" as const) : ("inactive" as const),
        invitedOn: new Date(m.joinedAt),
      };
    }

    return {
      id: m.id,
      email: m.email,
      firstName: m.firstName,
      lastName: m.lastName,
      status: m.status as "active" | "pending" | "expired",
      invitedOn: new Date(m.createdAt),
    };
  });

  const inviteError = getDisplayErrorMessage(createInvitation.error);

  const practiceNameError = getDisplayErrorMessage(updatePractice.error);

  const membersLoadError = getDisplayErrorMessage(membersQuery.error);

  const savePracticeName = useCallback(
    async (name: string) => {
      await updatePractice.mutateAsync({
        id: practiceId,
        data: { name },
      });

      await queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
      toast.success("Practice name updated.");
    },
    [practiceId, queryClient, updatePractice],
  );

  const sendInvite = useCallback(
    async (values: InviteMemberValues) => {
      setInviteSent(false);

      await createInvitation.mutateAsync({
        data: {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
        },
      });

      await queryClient.invalidateQueries({
        queryKey: getListPracticeMembersQueryKey(practiceId),
      });

      setInviteSent(true);
      toast.success("Invitation sent.");
    },
    [createInvitation, practiceId, queryClient],
  );

  return {
    practiceName: practice?.name ?? "",
    members,
    isLoadingMembers: membersQuery.isLoading,
    membersLoadError,
    isSavingPracticeName: updatePractice.isPending,
    isSendingInvite: createInvitation.isPending,
    inviteSent,
    inviteError,
    practiceNameError,
    onSavePracticeName: savePracticeName,
    onSendInvite: sendInvite,
  };
}
